import { calculateTimeAsPerTimeZone } from 'components/__v2__/calculateTimeAsPerTimeZone';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
} from 'date-fns';
import { getLocalTimeZone } from 'utils/dateAndTimeFormat';

type DateFormat =
  | 'year_only'
  | 'date_only'
  | 'date_with_time'
  | 'date_with_day'
  | 'date_with_day_and_time'
  | 'date_with_month'
  | 'year_month_day'
  | 'date_with_day_and_month';

export function formatDate(
  date: Date | string,
  formatType: DateFormat,
): string {
  let dateFormat: string;

  switch (formatType) {
    case 'year_only':
      dateFormat = 'yyyy';
      break;
    case 'date_only':
      dateFormat = 'dd/MM/yyyy';
      break;
    case 'date_with_time':
      dateFormat = 'dd/MM/yyyy, hh:mm a';
      break;
    case 'date_with_day':
      dateFormat = 'eee, dd/MM/yyyy';
      break;
    case 'date_with_day_and_time':
      dateFormat = 'eee, dd/MM/yyyy, hh:mm a';
      break;
    case 'date_with_month':
      dateFormat = 'yyyy-MM';
      break;
    case 'year_month_day':
      dateFormat = 'yyyy-MM-dd';
      break;
    case 'date_with_day_and_month':
      dateFormat = 'd MMM yyyy';
      break;
    default:
      throw new Error(`Unknown format type: ${formatType}`);
  }

  return format(new Date(date), dateFormat);
}

export const formatDateWithLocalTimezone = (date: string) => {
  const timezone = getLocalTimeZone();

  const formattedDateWithTimeZone = calculateTimeAsPerTimeZone(
    date ? new Date(date) : new Date(),
    timezone,
  );

  return `${formatDate(
    formattedDateWithTimeZone,
    'date_with_day_and_time',
  )} (${timezone})`;
};

export const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':');
  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));
  return format(date, 'hh:mm a');
};

export const getCurrentMonth = () => {
  const now = new Date();
  return {
    start: startOfMonth(now).toISOString(),
    end: endOfMonth(now).toISOString(),
    subtitle: `${format(startOfMonth(now), 'd MMM yyyy')} - ${format(
      endOfMonth(now),
      'd MMM yyyy',
    )}`,
  };
};

export const getLastMonth = () => {
  const lastMonth = addMonths(new Date(), -1);
  return {
    start: startOfMonth(lastMonth).toISOString(),
    end: endOfMonth(lastMonth).toISOString(),
    subtitle: format(lastMonth, 'MMM yyyy'),
  };
};

export const getCurrentQuarter = () => {
  const now = new Date();
  return {
    start: startOfQuarter(now).toISOString(),
    end: endOfQuarter(now).toISOString(),
    subtitle: `${format(startOfQuarter(now), 'MMM yyyy')} - ${format(
      endOfQuarter(now),
      'MMM yyyy',
    )}`,
  };
};

export const getLastQuarter = () => {
  const lastQuarter = subQuarters(new Date(), 1);
  return {
    start: startOfQuarter(lastQuarter).toISOString(),
    end: endOfQuarter(lastQuarter).toISOString(),
    subtitle: `${format(startOfQuarter(lastQuarter), 'MMM yyyy')} - ${format(
      endOfQuarter(lastQuarter),
      'MMM yyyy',
    )}`,
  };
};

export const getCurrentFinancialYear = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const startYear = now.getMonth() < 6 ? currentYear - 1 : currentYear;
  const start = new Date(startYear, 6, 1);
  const end = new Date();

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    subtitle: `1 July ${startYear} - ${format(end, 'd MMM yyyy')}`,
  };
};

export const getLastFinancialYear = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const startYear = now.getMonth() < 6 ? currentYear - 2 : currentYear - 1;
  const endYear = startYear + 1;
  const start = new Date(startYear, 6, 1);
  const end = new Date(endYear, 5, 30);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    subtitle: `1 July ${startYear} - 30 June ${endYear}`,
  };
};
